import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { Icon, formatDateTime } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { afterDashboardSignOut } from "@apps/dashboard/lib/after-dashboard-sign-out";
import {
  fetchAuthSessions,
  revokeAuthSessionToken,
  revokeOtherAuthSessions,
  type AuthSessionRow,
} from "@apps/dashboard/lib/auth-sessions";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { useMemo, useState } from "react";

const SESSIONS_QUERY_KEY = ["better-auth", "sessions"] as const;

function summarizeUserAgent(ua: string | null | undefined, unknownLabel: string): string {
  if (!ua || !ua.trim()) return unknownLabel;
  const s = ua.trim();
  if (s.length <= 96) return s;
  return `${s.slice(0, 93)}…`;
}

function isCurrentSession(
  row: AuthSessionRow,
  currentId: string | undefined,
  currentToken: string | undefined,
): boolean {
  if (currentToken && row.token === currentToken) return true;
  if (currentId && row.id === currentId) return true;
  return false;
}

export default function SettingsDevicesPage(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const { data: sessionData } = authClient.useSession();

  const currentSession = sessionData?.session as
    | { id?: string; token?: string }
    | undefined;
  const currentId = typeof currentSession?.id === "string" ? currentSession.id : undefined;
  const currentToken = typeof currentSession?.token === "string" ? currentSession.token : undefined;

  const sessionsQuery = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: fetchAuthSessions,
  });

  const sortedSessions = useMemo(() => {
    const list = sessionsQuery.data ?? [];
    return [...list].sort((a, b) => {
      const aCur = isCurrentSession(a, currentId, currentToken);
      const bCur = isCurrentSession(b, currentId, currentToken);
      if (aCur && !bCur) return -1;
      if (!aCur && bCur) return 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [sessionsQuery.data, currentId, currentToken]);

  const revokeOneMut = useMutation({
    mutationFn: revokeAuthSessionToken,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    },
  });

  const revokeOthersMut = useMutation({
    mutationFn: revokeOtherAuthSessions,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    },
  });

  async function handleRevokeOne(row: AuthSessionRow): Promise<void> {
    setActionError(null);
    if (!globalThis.confirm?.(t("dashboardUi.devicesPage.signOutDeviceConfirm"))) return;
    const current = isCurrentSession(row, currentId, currentToken);
    try {
      await revokeOneMut.mutateAsync(row.token);
      if (current) {
        await authClient.signOut();
        afterDashboardSignOut(qc);
        navigate(dashboardPaths.login, { replace: true });
        return;
      }
    } catch {
      setActionError(t("dashboardUi.devicesPage.actionError"));
    }
  }

  async function handleRevokeOthers(): Promise<void> {
    setActionError(null);
    if (!globalThis.confirm?.(t("dashboardUi.devicesPage.signOutOthersConfirm"))) return;
    try {
      await revokeOthersMut.mutateAsync();
    } catch {
      setActionError(t("dashboardUi.devicesPage.actionError"));
    }
  }

  const hasOthers = sortedSessions.some((s) => !isCurrentSession(s, currentId, currentToken));

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.settingsDevices")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">{t("dashboardUi.devicesPage.subtitle")}</p>
      <p className="mt-3 font-body text-sm text-on-surface-variant">{t("dashboardUi.devicesPage.intro")}</p>

      {actionError ? (
        <p className="mt-4 rounded-xl border border-error/30 bg-error-container/20 px-4 py-3 font-body text-sm text-error">
          {actionError}
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="font-headline text-xs font-semibold"
          disabled={!hasOthers || revokeOthersMut.isPending}
          onClick={() => void handleRevokeOthers()}
        >
          {t("dashboardUi.devicesPage.signOutOthers")}
        </Button>
      </div>

      <section className="mt-8">
        {sessionsQuery.isLoading ? (
          <p className="font-body text-sm text-on-surface-variant">{t("common.loading")}</p>
        ) : sessionsQuery.isError ? (
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low px-4 py-3">
            <p className="font-body text-sm text-tertiary">{t("dashboardUi.devicesPage.loadError")}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 font-headline text-xs font-semibold"
              onClick={() => void sessionsQuery.refetch()}
            >
              {t("dashboardUi.states.retry")}
            </Button>
          </div>
        ) : sortedSessions.length === 0 ? (
          <p className="font-body text-sm text-on-surface-variant">{t("dashboardUi.devicesPage.empty")}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {sortedSessions.map((row) => {
              const current = isCurrentSession(row, currentId, currentToken);
              const browser = summarizeUserAgent(row.userAgent, t("dashboardUi.devicesPage.unknownBrowser"));
              return (
                <li
                  key={row.id}
                  className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest/60 px-4 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon name="Smartphone" className="size-5" strokeWidth={1.5} aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-headline text-sm font-bold text-on-surface">
                            {current ? t("dashboardUi.devicesPage.thisDevice") : browser}
                          </span>
                          {current ? (
                            <span className="rounded-full bg-secondary/15 px-2 py-0.5 font-headline text-[10px] font-bold uppercase tracking-wide text-secondary">
                              {t("dashboardUi.devicesPage.currentBadge")}
                            </span>
                          ) : null}
                        </div>
                        {current ? (
                          <p className="mt-1 break-all font-body text-xs text-on-surface-variant">{browser}</p>
                        ) : null}
                        <dl className="mt-2 space-y-1 font-body text-xs text-on-surface-variant">
                          <div className="flex flex-wrap gap-x-2">
                            <dt className="text-on-surface-variant/80">{t("dashboardUi.devicesPage.lastActive")}</dt>
                            <dd>{formatDateTime(row.updatedAt)}</dd>
                          </div>
                          <div className="flex flex-wrap gap-x-2">
                            <dt className="text-on-surface-variant/80">{t("dashboardUi.devicesPage.sessionStarted")}</dt>
                            <dd>{formatDateTime(row.createdAt)}</dd>
                          </div>
                          <div className="flex flex-wrap gap-x-2">
                            <dt className="text-on-surface-variant/80">{t("dashboardUi.devicesPage.expires")}</dt>
                            <dd>{formatDateTime(row.expiresAt)}</dd>
                          </div>
                          {row.ipAddress ? (
                            <div className="flex flex-wrap gap-x-2">
                              <dt className="text-on-surface-variant/80">{t("dashboardUi.devicesPage.ip")}</dt>
                              <dd className="font-mono">{row.ipAddress}</dd>
                            </div>
                          ) : null}
                        </dl>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant={current ? "destructive" : "outline"}
                      size="sm"
                      className="shrink-0 font-headline text-xs font-semibold"
                      disabled={revokeOneMut.isPending}
                      onClick={() => void handleRevokeOne(row)}
                    >
                      {t("dashboardUi.devicesPage.signOutDevice")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
